import { useEffect, useState } from 'react';
import { studyTypeRequiredLayers } from '@/api/endpoints';
import { hasPermission, type StudyTypeRequiredLayer } from '@/types';
import { useAuth } from '@/auth/AuthContext';
import { Icon } from './Icon';

/**
 * Karta „Wymagane warstwy mapowe (WMS/WMTS)" dla danego Study. Lista wynika ze słownika
 * warstw wymaganych per typ opracowania (StudyTypeRequiredLayer). Tylko do odczytu — konfiguracja
 * jest w panelu admina (Słowniki → Warstwy per typ). Link „Otwórz w mapach" prowadzi do kreatora,
 * który auto-włącza te warstwy.
 */
export function StudyRequiredLayersCard({ studyId }: { studyId: number }) {
  const { user } = useAuth();
  const canOpenMaps = hasPermission(user, 'MAPS_DOWNLOAD');
  const [rows, setRows] = useState<StudyTypeRequiredLayer[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    studyTypeRequiredLayers.forStudy(studyId)
      .then(setRows)
      .catch(e => setErr(e?.message ?? String(e)));
  }, [studyId]);

  // Nic do pokazania, jeśli dla typu nie zdefiniowano warstw — nie zaśmiecamy karty opracowania.
  if (!err && rows && rows.length === 0) return null;

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title flex items-center gap-2">
          <Icon name="map" className="w-4 h-4 text-water-600"/> Wymagane warstwy mapowe (WMS/WMTS)
        </div>
        {canOpenMaps && (
          <a href={`/mapy?studyId=${studyId}`} className="ml-auto btn btn-secondary btn-sm">
            <Icon name="map" className="w-3.5 h-3.5"/> Otwórz w mapach
          </a>
        )}
      </div>
      <div className="card-body !p-0">
        {err && <div className="p-4 text-red-500 text-[12px]">Błąd: {err}</div>}
        {!rows && !err && <div className="p-4 text-ink-400">Ładowanie…</div>}
        {rows && rows.length > 0 && (
          <table className="table-hw">
            <thead>
              <tr>
                <th>Warstwa</th>
                <th className="w-24">Protokół</th>
                <th className="w-32">Kategoria</th>
                <th className="w-28">Wymagana</th>
                <th>Uwaga</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td className="text-ink-900">
                    <div>{r.layerName}</div>
                    <div className="font-mono text-[11px] text-ink-500">{r.layerCode} · {r.layerSource}</div>
                  </td>
                  <td><span className="pill pill-earth">{r.layerProtocol}</span></td>
                  <td className="text-[11px] text-ink-500">{r.layerCategory}</td>
                  <td>{r.mandatory
                    ? <span className="pill pill-amber">obowiązkowa</span>
                    : <span className="text-ink-400 text-[11px]">zalecana</span>}</td>
                  <td className="text-[11px] text-ink-500 max-w-[280px]">{r.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
